import { Section, SectionHeading, FadeIn } from "@/components/Section";
import { TrendingUp, Repeat, BarChart3, Users2 } from "lucide-react";

export default function RoiDashboard() {
  return (
    <Section testId="roi-section" className="bg-white">
      <SectionHeading
        eyebrow="ROI"
        title="Turn programs into predictable revenue."
        intro="Every program you deploy becomes a measurable line on the dashboard — MRR, adoption, retention, and automation in one view."
      />

      <FadeIn>
        <div className="mt-14 rounded-2xl border border-slate-200 bg-slate-50/50 p-6 lg:p-10">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5 space-y-4">
              {[
                { icon: Repeat, label: "Monthly Recurring Revenue", value: "$184,320", sub: "+18.4% vs. last month" },
                { icon: TrendingUp, label: "Program adoption", value: "67%", sub: "Across active patient panel" },
                { icon: BarChart3, label: "Workflow automation", value: "94%", sub: "Of operational tasks auto-routed" },
                { icon: Users2, label: "Retention", value: "91%", sub: "Quarterly retention across programs" },
              ].map((m, i) => (
                <div
                  key={i}
                  data-testid={`roi-metric-${i}`}
                  className="flex items-center gap-4 rounded-xl bg-white border border-slate-200 p-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <m.icon className="w-4.5 h-4.5" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                      {m.label}
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <div className="font-display text-xl font-bold text-slate-900 tabular">{m.value}</div>
                      <div className="text-xs text-slate-500">{m.sub}</div>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-500 mt-3">Figures shown are illustrative — based on aggregated platform operating patterns.</p>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">MRR trajectory</div>
                    <div className="font-display text-lg font-semibold text-slate-900">Cumulative program revenue</div>
                  </div>
                  <div className="text-xs font-medium text-emerald-600">+112% YoY</div>
                </div>
                <BarStack />
                <div className="mt-4 grid grid-cols-4 gap-3 text-[11px] text-slate-500">
                  {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                    <div key={q} className="text-center font-medium">{q}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
    </Section>
  );
}

function BarStack() {
  const data = [
    [22, 14, 8],
    [28, 20, 12],
    [34, 24, 16],
    [40, 30, 22],
  ];
  return (
    <svg viewBox="0 0 400 160" className="w-full h-44 mt-4" aria-hidden>
      {[40, 80, 120].map((y) => (
        <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="#f1f5f9" strokeDasharray="3 4" />
      ))}
      {data.map((col, i) => {
        const x = 40 + i * 90;
        let y = 150;
        return (
          <g key={i}>
            {col.map((v, j) => {
              const h = v * 2.2;
              y -= h;
              const color = ["#0f172a", "#2563eb", "#93c5fd"][j];
              return <rect key={j} x={x} y={y} width="44" height={h} rx="3" fill={color} />;
            })}
          </g>
        );
      })}
    </svg>
  );
}
